import { Component, Input, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { MatDialog } from '@angular/material/dialog';
import { WidgetService } from '../../shared/services/widget.service';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '../../core/snack-bar.service';
import { AccountService } from '../../account/account.service';
import { Account } from '../../back-office/account.model';
import SessionConditionHelpers from '../../audience/session-condition-helpers';
import {
  SessionCondition,
  SessionConditionConfig,
  SessionConditionConfigGroup,
  SessionConditionGroup,
  SessionConditionType,
  WidgetSessionConditionsFlyoutResponse,
} from '../../audience/components/session-condition-group.model';
import { SessionConditionDialogComponent } from '../session-condition-dialog/session-condition-dialog.component';
import { Widget, WidgetComponentSubType, WidgetSimpleInfo } from '../../campaign/widgets.model';
import { AutomationHelpers } from '../../automations/automation-helpers';
import { Subscription } from 'rxjs';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { AccountUpgradePopupService } from 'app/account-upgrade/account-upgrade-popup/account-upgrade-popup.service';

@Component({
  selector: 'app-flyout-session-on-widget',
  templateUrl: './flyout-session-on-widget.component.html',
  styleUrls: ['./flyout-session-on-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutSessionOnWidgetComponent implements OnDestroy {
  private readonly logger: Logger;
  private _openingData: FlyoutOpeningData;
  public currentAccount: Account;
  public allConditionGroups: Array<SessionConditionGroup>;
  public allConditionsConfigGroups = [] as Array<SessionConditionConfigGroup>;
  public currentConditionGroup: SessionConditionGroup;
  public sessionConditions?: Array<SessionCondition>;
  public allWidgets?: Array<WidgetSimpleInfo>;
  public widget?: Widget;
  private getAccountSub: Subscription;
  private subscriptionArray: Subscription[] = [];

  @Input() public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.setUpConditionData();
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this._openingData;
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    private dialog: MatDialog,
    private readonly widgetService: WidgetService,
    private readonly translateService: TranslateService,
    private readonly snackbarService: SnackBarService,
    private readonly accountService: AccountService,
    private readonly accountUpgradePopupService: AccountUpgradePopupService,
  ) {
    this.logger = loggerFactory.get('FlyoutSessionOnWidgetComponent');

    this.getAccountSub = this.accountService.getCurrentAccount().subscribe((account: Account) => {
      this.currentAccount = account;
      this.initializeSupportedConditions();
    });
    this.initializeSupportedConditions();
  }

  public setUpConditionData() {
    if (this._openingData && this._openingData.widget) {
      this.allWidgets = this._openingData.widgets;
      if (this.allWidgets) {
        this.allWidgets.forEach(
          (w) => (w.dynamicName = AutomationHelpers.getDynamicWidgetName(w, this.translateService)),
        );
      }
      this.widget = this._openingData.widget;
      this.allConditionGroups = this._openingData.sessionConditionGroups;
      if (this.allConditionGroups) {
        this.allConditionGroups.forEach((conditionGroup: SessionConditionGroup) => {
          if (this.widget.sessionConditionGroupId === conditionGroup.id) {
            this.currentConditionGroup = conditionGroup;
          }
        });
      }

      if (this._openingData.sessionConditions) {
        this._openingData.sessionConditions.forEach((sessionCondition: SessionCondition) => {
          SessionConditionHelpers.translateSessionCondition(
            sessionCondition,
            false,
            this.translateService,
            this.allWidgets,
          );
        });
        this.sessionConditions = this._openingData.sessionConditions;
        this.populateConditionsWithConfig();
        this.evaluateAndDisableSessionConditions();
      } else {
        this.evaluateAndDisableSessionConditions();
      }
    }
  }

  private evaluateAndDisableSessionConditions(): void {
    if (this.allConditionsConfigGroups && (this.sessionConditions || this.currentConditionGroup)) {
      let allChosenConditions = [] as Array<SessionCondition>;
      if (this.sessionConditions) {
        allChosenConditions = allChosenConditions.concat(this.sessionConditions);
      }
      if (this.currentConditionGroup) {
        allChosenConditions = allChosenConditions.concat(this.currentConditionGroup.sessionConditions);
      }
      const usedConditionTypes = [] as Array<SessionConditionType>;
      let hasDeviceType = false;
      const deviceConditions = SessionConditionHelpers.DeviceSessionConditionTypes;
      allChosenConditions.forEach((c) => {
        if (deviceConditions.indexOf(c.type) !== -1) {
          hasDeviceType = true;
        }
        if (usedConditionTypes.indexOf(c.type) === -1) {
          usedConditionTypes.push(c.type);
        }
      });
      this.allConditionsConfigGroups.forEach((configGroup: SessionConditionConfigGroup) => {
        configGroup.conditions.forEach((condition) => {
          if (deviceConditions.indexOf(condition.type) !== -1 && hasDeviceType) {
            condition.disabled = true;
          } else if (allChosenConditions.map((condition) => condition.type).includes(condition.type)) {
            condition.disabled = true;
          } else {
            condition.disabled = undefined;
          }
        });
      });
    }
  }

  public removeSessionCondition(sessionCondition: SessionCondition, index: number, event: MouseEvent) {
    event.stopPropagation();
    if (!this.isRemoveSessionConditionDisabled(sessionCondition)) {
      this.sessionConditions.splice(index, 1);
      this.evaluateAndDisableSessionConditions();
    }
  }

  public isRemoveSessionConditionDisabled(sessionCondition: SessionCondition): boolean {
    const hasSubscriptionCheckboxList = WidgetComponentHelpers.stateHasProperty(
      this.widget.states,
      (x) => x.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox,
    );
    if (hasSubscriptionCheckboxList && sessionCondition.type === SessionConditionType.IdentifiedEmailMarketing) {
      return true;
    }
    return false;
  }

  public removeSessionConditionDisabledTooltipTranslation(sessionCondition: SessionCondition): string {
    if (this.isRemoveSessionConditionDisabled(sessionCondition)) {
      return 'audience.session-condition-groups.subscription-checkbox-tooltip';
    }
    throw new Error('Tooltip-translation not implemented');
  }

  public openSessionConditionDialog(sessionCondition: SessionCondition, index: number) {
    const editAudienceFilterDialogRef = this.dialog.open(SessionConditionDialogComponent, {
      panelClass: 'session-condition-dialog-component-container',
      disableClose: true,
      autoFocus: false,
      width: sessionCondition.type === SessionConditionType.JsExpression ? '640px' : '480px',
      data: {
        sessionCondition: sessionCondition,
        allWidgets: this.allWidgets,
        accountDomain: this.currentAccount ? this.currentAccount.urls[0] : undefined,
      },
    });

    editAudienceFilterDialogRef
      .afterClosed()
      .subscribe((result: { sessionCondition: SessionCondition; skipTranslatingValue?: boolean }) => {
        if (result) {
          result.sessionCondition = SessionConditionHelpers.translateSessionCondition(
            result.sessionCondition,
            false,
            this.translateService,
            this.allWidgets,
          );
          this.sessionConditions[index] = result.sessionCondition;
          this.evaluateAndDisableSessionConditions();
        }
      });
  }

  public setConditionGroupToWidget(conditionGroup: SessionConditionGroup) {
    this.currentConditionGroup = conditionGroup;
  }

  public isDisabled(config: SessionConditionConfig): boolean {
    if (config?.disabledOnTiers?.includes(this.currentAccount.tier)) {
      return true;
    }
    return false;
  }

  public addConditionToWidget(config: SessionConditionConfig) {
    if (this.isDisabled(config)) {
      this.accountUpgradePopupService.openPopup('session-condition-' + config.type);
      return;
    }

    if (config.defaultOperator) {
      const createSessionConditionDialogRef = this.dialog.open(SessionConditionDialogComponent, {
        panelClass: 'session-condition-dialog-component-container',
        disableClose: true,
        autoFocus: false,
        width: config.type === SessionConditionType.JsExpression ? '640px' : '480px',
        data: {
          newConditionConfig: config,
          allWidgets: this.allWidgets,
          accountDomain: this.currentAccount ? this.currentAccount.urls[0] : undefined,
        },
      });

      createSessionConditionDialogRef
        .afterClosed()
        .subscribe((result: { sessionCondition: SessionCondition; skipTranslatingValue?: boolean }) => {
          if (result) {
            result.sessionCondition = SessionConditionHelpers.translateSessionCondition(
              result.sessionCondition,
              result.skipTranslatingValue === true,
              this.translateService,
              this.allWidgets,
            );
            this.sessionConditions.push(result.sessionCondition);
            this.evaluateAndDisableSessionConditions();
          }
        });
    } else {
      let condition = SessionConditionHelpers.conditionConfigToCondition(config);
      condition = SessionConditionHelpers.translateSessionCondition(
        condition,
        true,
        this.translateService,
        this.allWidgets,
      );
      this.sessionConditions.push(condition);
      this.evaluateAndDisableSessionConditions();
    }
  }

  public saveSessionConditions() {
    const sessionConditionGroupId = this.currentConditionGroup ? this.currentConditionGroup.id : undefined;
    this.widgetService
      .createOrUpdateWidgetConditions(this.widget, sessionConditionGroupId, this.sessionConditions)
      .subscribe(
        () => {
          this.widget.sessionConditionGroupId = sessionConditionGroupId;
          this.widget.conditions = this.sessionConditions;
          this.snackbarService.success('audience.messages.widget-session-condition-updated');
          this.flyoutService.closeWidgetSessionConditionsFlyout({
            sessionConditions: this.sessionConditions,
            sessionConditionGroupId: sessionConditionGroupId,
            widgetId: this.widget.id,
          } as WidgetSessionConditionsFlyoutResponse);
        },
        (error) => {
          this.snackbarService.error('audience.messages.widget-session-condition-failed');
          console.error('Error that should break the page, could not update condition: ' + JSON.stringify(error));
        },
      );
  }

  private populateConditionsWithConfig(): void {
    this.sessionConditions.forEach((condition: SessionCondition) => {
      condition.conditionConfig = SessionConditionHelpers.getSessionConditionConfig(condition.type);
    });
  }

  public closeFlyout() {
    this.flyoutService.close();
  }

  private initializeSupportedConditions() {
    const loggedInDetectionEnabled = this.currentAccount?.loggedInDetection != null;
    this.allConditionsConfigGroups = SessionConditionHelpers.getAllSessionConditionConfigGroups().map((group) => ({
      ...group,
      conditions: group.conditions.filter(
        (condition) => condition.type !== SessionConditionType.LoggedIn || loggedInDetectionEnabled,
      ),
    }));
    this.evaluateAndDisableSessionConditions();
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.getAccountSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
